/* eslint-disable */
import { useMemo, useState } from 'react';
import type { Ticket, SRDetails, StabilizationBug } from '../types/release';

export type FilterOptions = {
  status?: string[];
  type?: string[];
  assignee?: string[];
  productScope?: string[];
  swTeam?: string[];
  hasValidationIssues?: boolean;
  isLate?: boolean;
};

export type SortOption = {
  field: 'id' | 'status' | 'assignee' | 'title';
  direction: 'asc' | 'desc';
};

export const useRelease = (
  customerValue: SRDetails[],
  rdActivities: Ticket[],
  sl3Tickets: Ticket[],
  stabilizationBugs: StabilizationBug[]
) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sortOption, setSortOption] = useState<SortOption>({ field: 'id', direction: 'asc' });

  // Extract all unique values for filter options
  const filterOptions = useMemo(() => {
    const allTickets: Ticket[] = [
      ...rdActivities,
      ...sl3Tickets,
      ...customerValue.flatMap(sr => sr.engineering_tickets || []),
    ];

    const statuses = new Set<string>();
    const types = new Set<string>();
    const assignees = new Set<string>();
    const productScopes = new Set<string>();
    const swTeamSet = new Set<string>();
    let hasUnassignedSwTeam = false;

    allTickets.forEach(ticket => {
      if (ticket.status) statuses.add(ticket.status);
      if (ticket.type) types.add(ticket.type);
      if (ticket.assignee) assignees.add(ticket.assignee);
      if (ticket.product_scope) productScopes.add(ticket.product_scope);
      if (ticket.sw_team) {
        swTeamSet.add(ticket.sw_team);
      } else if (ticket.type !== 'Epic' && ticket.type !== 'Sub-task') {
        hasUnassignedSwTeam = true;
      }
    });

    // Also scan stabilization bugs for SW Team values
    stabilizationBugs.forEach(bug => {
      if (bug.sw_team) {
        swTeamSet.add(bug.sw_team);
      } else {
        hasUnassignedSwTeam = true;
      }
    });

    const swTeams = [...swTeamSet].sort();
    if (hasUnassignedSwTeam) {
      swTeams.push('Unassigned');
    }

    return {
      statuses: Array.from(statuses).sort(),
      types: Array.from(types).sort(),
      assignees: Array.from(assignees).sort(),
      productScopes: Array.from(productScopes).sort(),
      swTeams,
    };
  }, [customerValue, rdActivities, sl3Tickets, stabilizationBugs]);

  // Search function
  const matchesSearch = (ticket: Ticket): boolean => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.id.toLowerCase().includes(query) ||
      ticket.title.toLowerCase().includes(query)
    );
  };

  // Filter function
  const matchesFilters = (ticket: Ticket): boolean => {
    if (filters.status && filters.status.length > 0 && !filters.status.includes(ticket.status)) {
      return false;
    }
    if (filters.type && filters.type.length > 0 && !filters.type.includes(ticket.type)) {
      return false;
    }
    if (filters.assignee && filters.assignee.length > 0) {
      if (!ticket.assignee || !filters.assignee.includes(ticket.assignee)) {
        return false;
      }
    }
    if (filters.productScope && filters.productScope.length > 0) {
      if (!ticket.product_scope || !filters.productScope.includes(ticket.product_scope)) {
        return false;
      }
    }
    // SW Team filtering: skip check for Epic and Sub-task (they don't have sw_team)
    if (filters.swTeam && filters.swTeam.length > 0 && ticket.type !== 'Epic' && ticket.type !== 'Sub-task') {
      const isUnassignedSelected = filters.swTeam.includes('Unassigned');
      const hasSwTeam = ticket.sw_team && filters.swTeam.includes(ticket.sw_team);
      if (!hasSwTeam && !(isUnassignedSelected && !ticket.sw_team)) {
        return false;
      }
    }
    if (filters.hasValidationIssues === true && ticket.validation_issues.length === 0) {
      return false;
    }
    if (filters.hasValidationIssues === false && ticket.validation_issues.length > 0) {
      return false;
    }
    if (filters.isLate === true && !ticket.is_late) {
      return false;
    }
    if (filters.isLate === false && ticket.is_late) {
      return false;
    }
    return true;
  };

  // Helper function to check if any descendant matches SW Team filter
  const hasMatchingDescendant = (ticket: Ticket): boolean => {
    if (!filters.swTeam || filters.swTeam.length === 0) {
      return true; // No SW Team filter active
    }
    
    // Check if this ticket itself matches (if it's not an Epic/Sub-task)
    if (ticket.type !== 'Epic' && ticket.type !== 'Sub-task') {
      const isUnassignedSelected = filters.swTeam.includes('Unassigned');
      const hasSwTeam = ticket.sw_team && filters.swTeam.includes(ticket.sw_team);
      if (hasSwTeam || (isUnassignedSelected && !ticket.sw_team)) {
        return true;
      }
    }
    
    // Check children recursively
    if (ticket.children && ticket.children.length > 0) {
      return ticket.children.some((child: Ticket | { id: string; title: string; status: string; is_late?: boolean; assignee?: string }) => {
        // Handle simplified child objects (they don't have sw_team, so skip)
        if (!('sw_team' in child) && !('type' in child)) {
          return false;
        }
        // Handle full Ticket objects
        const childTicket = child as Ticket;
        return hasMatchingDescendant(childTicket);
      });
    }
    
    return false;
  };

  // Sort function
  const sortTickets = (tickets: Ticket[]): Ticket[] => {
    return [...tickets].sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortOption.field) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'assignee':
          aValue = a.assignee || '';
          bValue = b.assignee || '';
          break;
        case 'title':
          aValue = a.title || '';
          bValue = b.title || '';
          break;
        default:
          return 0;
      }

      const comparison = aValue.localeCompare(bValue);
      return sortOption.direction === 'asc' ? comparison : -comparison;
    });
  };

  // Filter and sort customer value
  const filteredCustomerValue = useMemo(() => {
    const filterTicketWithHierarchy = (ticket: Ticket): Ticket | null => {
      // Filter children first (recursively)
      let filteredChildren: Ticket[] = [];
      if (ticket.children && ticket.children.length > 0) {
        filteredChildren = ticket.children
          .map((child: Ticket | { id: string; title: string; status: string; is_late?: boolean; assignee?: string }) => {
            // Handle simplified child objects - they don't have filtering capabilities
            if (!('sw_team' in child) && !('type' in child)) {
              return null;
            }
            return filterTicketWithHierarchy(child as Ticket);
          })
          .filter((child): child is Ticket => child !== null);
      }
      
      // For Epics: keep if they have matching descendants (when SW Team filter is active)
      if (ticket.type === 'Epic') {
        if (filters.swTeam && filters.swTeam.length > 0) {
          if (!hasMatchingDescendant(ticket)) {
            return null;
          }
        }
        // Epics pass matchesFilters (they skip SW Team check), but we still need to check descendants
        const ticketMatches = matchesSearch(ticket) && matchesFilters(ticket);
        if (!ticketMatches && filteredChildren.length === 0) {
          return null;
        }
        return { ...ticket, children: filteredChildren };
      }
      
      // For other tickets: must match filters
      const ticketMatches = matchesSearch(ticket) && matchesFilters(ticket);
      if (!ticketMatches) {
        return null;
      }
      
      return { ...ticket, children: filteredChildren };
    };

    const filtered = customerValue.map(sr => ({
      ...sr,
      engineering_tickets: sortTickets(
        (sr.engineering_tickets || [])
          .map(filterTicketWithHierarchy)
          .filter((ticket): ticket is Ticket => ticket !== null)
      ),
    }));

    // Remove SRs with no matching engineering tickets
    return filtered.filter(sr => sr.engineering_tickets.length > 0);
  }, [customerValue, searchQuery, filters, sortOption]);

  // Filter and sort R&D activities
  const filteredRDActivities = useMemo(() => {
    const filterTicketRecursive = (ticket: Ticket): Ticket | null => {
      // Filter children first (recursively)
      let filteredChildren: Ticket[] = [];
      if (ticket.children && ticket.children.length > 0) {
        filteredChildren = ticket.children
          .map((child: Ticket | { id: string; title: string; status: string; is_late?: boolean; assignee?: string }) => {
            // Handle simplified child objects
            if (!('sw_team' in child) && !('type' in child)) {
              return null;
            }
            return filterTicketRecursive(child as Ticket);
          })
          .filter((child): child is Ticket => child !== null);
      }
      
      // For Epics: keep if they have matching descendants (when SW Team filter is active)
      if (ticket.type === 'Epic') {
        if (filters.swTeam && filters.swTeam.length > 0) {
          if (!hasMatchingDescendant(ticket)) {
            return null;
          }
        }
        // Epics pass matchesFilters (they skip SW Team check), but we still need to check descendants
        const ticketMatches = matchesSearch(ticket) && matchesFilters(ticket);
        if (!ticketMatches && filteredChildren.length === 0) {
          return null;
        }
        return { ...ticket, children: filteredChildren };
      }
      
      // For other tickets: must match filters
      const ticketMatches = matchesSearch(ticket) && matchesFilters(ticket);
      if (!ticketMatches) {
        return null;
      }
      
      return { ...ticket, children: filteredChildren };
    };

    return sortTickets(
      rdActivities
        .map(filterTicketRecursive)
        .filter((ticket): ticket is Ticket => ticket !== null)
    );
  }, [rdActivities, searchQuery, filters, sortOption]);

  // Filter and sort SL3 tickets
  const filteredSL3Tickets = useMemo(() => {
    return sortTickets(
      sl3Tickets.filter(ticket => 
        matchesSearch(ticket) && matchesFilters(ticket)
      )
    );
  }, [sl3Tickets, searchQuery, filters, sortOption]);

  // Filter stabilization bugs
  const filteredStabilizationBugs = useMemo(() => {
    let filtered = stabilizationBugs;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(bug =>
        bug.id.toLowerCase().includes(query) ||
        bug.title.toLowerCase().includes(query)
      );
    }

    // Apply SW Team filter
    if (filters.swTeam && filters.swTeam.length > 0) {
      filtered = filtered.filter(bug => {
        const isUnassignedSelected = filters.swTeam!.includes('Unassigned');
        const hasSwTeam = bug.sw_team && filters.swTeam!.includes(bug.sw_team);
        return hasSwTeam || (isUnassignedSelected && !bug.sw_team);
      });
    }

    return filtered;
  }, [stabilizationBugs, searchQuery, filters]);

  return {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    filterOptions,
    filteredCustomerValue,
    filteredRDActivities,
    filteredSL3Tickets,
    filteredStabilizationBugs,
  };
};
